import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, CheckCircle2, Target, Users, ShieldCheck, TrendingUp } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { heroStats, whyChoose, tools } from '../mock';

const values = [
  { icon: Target, title: 'Precision First', text: 'Every line item is sourced, justified, and re-verified — not pulled from gut feel.' },
  { icon: ShieldCheck, title: 'Owner Mindset', text: 'We price your job like we own it. Margins, risk, and reality are baked into every number.' },
  { icon: Users, title: 'Senior Estimators', text: 'A senior, not a junior. You always work with someone who has sat in a real bid room.' },
  { icon: TrendingUp, title: 'Continuous Improvement', text: 'Every job feeds our database — so the next estimate is sharper than the last.' },
];

const team = [
  { name: 'Daniel Okafor', role: 'Founder & Chief Estimator', exp: '22 yrs in commercial preconstruction', img: 'https://images.unsplash.com/photo-1574757987642-5755f0839101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85' },
  { name: 'Rachel Lindgren', role: 'Director of Preconstruction', exp: '18 yrs, ENR Top-400 GC background', img: 'https://images.unsplash.com/photo-1774600166588-1db444bda799?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3QlMjBlbmdpbmVlcnxlbnwwfHx8fDE3Nzg1ODE1NTB8MA&ixlib=rb-4.1.0&q=85' },
  { name: 'Hector Salinas', role: 'Senior MEP Estimator', exp: '15 yrs in electrical & mechanical', img: 'https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85' },
  { name: 'Yumiko Tanaka', role: 'Civil & Site Estimator', exp: '12 yrs, DOT and federal projects', img: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85' },
];

export default function About() {
  return (
    <>
      {/* Page hero */}
      <section className="relative pt-40 pb-20 bg-[#0B1B33] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-4">About Core Estimation LLC</div>
          <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.05] max-w-3xl">
            Built by estimators, for the people who run bid rooms.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            We are an independent construction estimating and material takeoff firm headquartered in McLean, Virginia,
            with field teams in New York and Los Angeles. Our only job is to make your bid sharper than the next guy’s.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3ODU4MTU0Mnww&ixlib=rb-4.1.0&q=85"
                alt="Construction site"
                className="rounded-3xl w-full h-[480px] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-amber-500 rounded-2xl p-6 max-w-[200px] shadow-2xl">
                <div className="text-4xl font-bold text-[#0B1B33]">12+</div>
                <div className="text-sm font-semibold text-[#0B1B33] mt-1">Years estimating across 38 states</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Our story</Badge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">
              From one estimator with a laptop to a national preconstruction team.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Core Estimation LLC was founded in 2014 by Daniel Okafor after a 12-year run inside the preconstruction
              departments of two ENR Top-400 general contractors. The thesis was simple: small and mid-sized contractors
              deserve the same caliber of estimating their largest competitors get — without the overhead of a full
              in-house department.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Twelve years later, we have priced over $4 billion in construction work across 38 states, from custom
              residential infills to nine-figure mixed-use developments. Every estimate is still reviewed by a senior
              estimator before it leaves our office.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {['Independent and privately held','ASPE and AACE member firm','EMR-rated subcontractor relationships in 38 states','Owner-led, not VC-owned'].map((line, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {heroStats.map((s, i) => (
            <div key={i} className="text-center p-8 bg-white rounded-2xl border border-slate-100">
              <div className="text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight">{s.value}</div>
              <div className="text-sm text-slate-500 mt-2 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-[#0B1B33] text-amber-400 px-3 py-1 hover:bg-[#0B1B33]">What we stand for</Badge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight">Four values, zero exceptions.</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Card key={i} className="p-7 rounded-2xl border-slate-100 hover:border-amber-300 hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                  <v.icon className="w-5 h-5 text-[#0B1B33]" />
                </div>
                <h3 className="mt-5 font-bold text-[#0B1B33] text-lg">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Leadership</Badge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">Estimators who’ve sat on your side of the table.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <Card key={i} className="group bg-white rounded-2xl overflow-hidden border-slate-100 hover:shadow-xl transition">
                <div className="relative h-72 overflow-hidden">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="font-bold text-lg">{m.name}</div>
                    <div className="text-xs text-amber-400 uppercase tracking-wider mt-0.5">{m.role}</div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-slate-600">{m.exp}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Why teams choose us</Badge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight">The math behind the trust.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChoose.map((w, i) => (
              <Card key={i} className="p-7 rounded-2xl border-slate-100 hover:border-amber-300 hover:shadow-lg transition-all bg-white">
                <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                  <w.icon className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="mt-5 font-bold text-[#0B1B33] text-lg">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-20 bg-[#0B1B33]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Award className="w-10 h-10 text-amber-400 mx-auto" />
          <h2 className="mt-4 text-3xl md:text-4xl font-bold text-white tracking-tight">A modern preconstruction stack.</h2>
          <p className="mt-3 text-slate-400 max-w-2xl mx-auto">We use the tools your team already speaks, plus a few your team probably wishes it had.</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {tools.map((t, i) => (
              <div key={i} className="text-slate-300 hover:text-amber-400 font-semibold text-lg transition cursor-default">{t}</div>
            ))}
          </div>
          <Link to="/contact" className="inline-block mt-12">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl">
              Work with us <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
